import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowUp, ArrowDown, Equals, Clock, ArrowsClockwise, ChartLineUp,
  CurrencyDollar, Users, ShoppingCart, ArrowSquareOut, Package, Percent
} from "@phosphor-icons/react";
import CpiChart from "@/components/CpiChart";

const CATEGORY_CONFIG = {
  gdp_current: { icon: CurrencyDollar, color: "#3b82f6", label: "GDP (Current USD)", accent: "blue", format: "currency_b" },
  gdp_growth: { icon: ChartLineUp, color: "#10b981", label: "Real GDP Growth", accent: "emerald", format: "percent" },
  gdp_per_capita: { icon: Users, color: "#8b5cf6", label: "GDP Per Capita", accent: "violet", format: "currency" },
  inflation: { icon: Percent, color: "#ef4444", label: "Inflation Rate (CPI)", accent: "red", format: "percent" },
  current_account: { icon: ArrowSquareOut, color: "#06b6d4", label: "Current Account (% GDP)", accent: "cyan", format: "percent" },
  govt_debt: { icon: CurrencyDollar, color: "#f59e0b", label: "Govt Gross Debt (% GDP)", accent: "amber", format: "percent" },
  unemployment_imf: { icon: Users, color: "#f97316", label: "Unemployment Rate", accent: "orange", format: "percent" },
  govt_expenditure: { icon: ShoppingCart, color: "#ec4899", label: "Govt Expenditure (% GDP)", accent: "pink", format: "percent" },
  govt_revenue: { icon: Package, color: "#14b8a6", label: "Govt Revenue (% GDP)", accent: "teal", format: "percent" },
};

function fmtGdp(val, format) {
  if (val === null || val === undefined) return "--";
  if (format === "currency_b") {
    if (val >= 1000) return `$${(val / 1000).toFixed(2)}T`;
    return `$${val.toFixed(1)}B`;
  }
  if (format === "currency") {
    return `$${val.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  }
  return `${val.toFixed(2)}%`;
}

function ChangeIndicator({ value, suffix = "%" }) {
  if (value === null || value === undefined) return <span className="text-zinc-600 text-xs font-data">--</span>;
  const isPositive = value > 0;
  const isZero = value === 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-data font-medium ${isZero ? "text-zinc-400" : isPositive ? "text-emerald-400" : "text-red-400"}`}>
      {isPositive ? <ArrowUp size={10} weight="bold" /> : isZero ? <Equals size={10} /> : <ArrowDown size={10} weight="bold" />}
      {isPositive ? "+" : ""}{value.toFixed(2)}{suffix}
    </span>
  );
}

function HeroCard({ category, data }) {
  const cfg = CATEGORY_CONFIG[category];
  if (!cfg || !data) return null;
  const Icon = cfg.icon;

  return (
    <div className="bg-zinc-900/50 border border-amber-500/25 rounded-sm p-5 space-y-4" data-testid={`gdp-hero-${category}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-sm flex items-center justify-center" style={{ backgroundColor: `${cfg.color}15` }}>
            <Icon size={16} weight="bold" style={{ color: cfg.color }} />
          </div>
          <div>
            <h3 className="text-sm font-heading font-bold text-zinc-100">{cfg.label}</h3>
            <p className="text-[10px] text-zinc-500">{data.latest_year}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-xl font-data font-bold text-zinc-50">{fmtGdp(data.latest_value, cfg.format)}</p>
          <p className="text-[9px] text-zinc-600 uppercase tracking-wider">{cfg.format === "currency_b" ? "USD" : cfg.format === "currency" ? "USD" : "% RATE"}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3">
        <div className="bg-zinc-950/50 border border-amber-500/15 rounded-sm p-3">
          <p className="text-[9px] text-zinc-500 uppercase tracking-wider mb-1">Year-over-Year Change</p>
          <ChangeIndicator value={data.yoy_change} />
        </div>
      </div>

      <div>
        <p className="text-[9px] text-zinc-500 uppercase tracking-wider mb-2">Historical Trend</p>
        <CpiChart data={data.history} color={cfg.color} height={130} />
      </div>
    </div>
  );
}

function ComponentCard({ category, data }) {
  const cfg = CATEGORY_CONFIG[category];
  if (!cfg || !data) return null;
  const Icon = cfg.icon;

  return (
    <div className="bg-zinc-900/40 border border-amber-500/20 rounded-sm p-4 space-y-3 hover:border-zinc-700 transition-colors" data-testid={`gdp-component-${category}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon size={14} weight="bold" style={{ color: cfg.color }} />
          <span className="text-xs font-medium text-zinc-300">{cfg.label}</span>
        </div>
        <span className="text-sm font-data font-bold text-zinc-100">{fmtGdp(data.latest_value, cfg.format)}</span>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <p className="text-[9px] text-zinc-600 mb-0.5">YoY</p>
          <ChangeIndicator value={data.yoy_change} />
        </div>
        <p className="text-[9px] text-zinc-500">{data.latest_year}</p>
      </div>

      <CpiChart data={data.history} color={cfg.color} height={80} />
    </div>
  );
}

export default function GdpReport() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState("overview");

  useEffect(() => { fetchGdp(); }, []);

  const fetchGdp = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await axios.get(`${API}/gdp`);
      setReport(resp.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load GDP data");
    } finally {
      setLoading(false);
    }
  };

  const cats = report?.categories || {};
  const heroKeys = ["gdp_current", "gdp_growth", "gdp_per_capita"];
  const componentKeys = ["inflation", "current_account", "govt_debt", "unemployment_imf", "govt_expenditure", "govt_revenue"];
  const allAvailable = Object.entries(cats).filter(([k]) => CATEGORY_CONFIG[k]);

  return (
    <div className="p-5 space-y-5 max-w-[1600px]" data-testid="gdp-report">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-zinc-50 tracking-tight">Gross Domestic Product</h1>
          <p className="text-xs text-zinc-500 mt-1 flex items-center gap-1.5">
            <CurrencyDollar size={12} />
            U.S. GDP output and growth measures from IMF
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Tabs value={view} onValueChange={setView}>
            <TabsList className="h-7 bg-zinc-800/50 p-0.5">
              <TabsTrigger value="overview" className="text-[10px] px-2.5 py-1 h-6 data-[state=active]:bg-zinc-700" data-testid="gdp-tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="components" className="text-[10px] px-2.5 py-1 h-6 data-[state=active]:bg-zinc-700" data-testid="gdp-tab-components">All Components</TabsTrigger>
            </TabsList>
          </Tabs>
          <button onClick={fetchGdp} disabled={loading} className="flex items-center gap-1.5 text-[10px] text-zinc-400 hover:text-zinc-200 transition-colors px-2 py-1 border border-zinc-800 rounded-sm" data-testid="gdp-refresh-btn">
            <ArrowsClockwise size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-sm p-4 text-center" data-testid="gdp-error">
          <p className="text-xs text-red-400">{error}</p>
          <button onClick={fetchGdp} className="text-[10px] text-red-300 underline mt-1">Try Again</button>
        </div>
      )}

      {loading && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">{[1,2,3].map(i => <Skeleton key={i} className="h-[320px] bg-zinc-900/50" />)}</div>
        </div>
      )}

      {!loading && !error && view === "overview" && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="gdp-hero-grid">
            {heroKeys.map(key => cats[key] && <HeroCard key={key} category={key} data={cats[key]} />)}
          </div>

          {cats.gdp_current && (
            <div className="bg-zinc-900/30 border border-amber-500/15 rounded-sm p-4" data-testid="gdp-summary">
              <div className="flex items-center flex-wrap gap-6">
                <div>
                  <p className="text-[9px] text-zinc-500 uppercase tracking-wider">Total GDP</p>
                  <p className="text-lg font-data font-bold text-blue-400">{fmtGdp(cats.gdp_current?.latest_value, "currency_b")}</p>
                </div>
                {cats.gdp_growth && (
                  <div>
                    <p className="text-[9px] text-zinc-500 uppercase tracking-wider">Growth Rate</p>
                    <p className={`text-lg font-data font-bold ${(cats.gdp_growth.latest_value || 0) > 0 ? "text-emerald-400" : "text-red-400"}`}>
                      {cats.gdp_growth.latest_value?.toFixed(2)}%
                    </p>
                  </div>
                )}
                {cats.gdp_per_capita && (
                  <div>
                    <p className="text-[9px] text-zinc-500 uppercase tracking-wider">Per Capita</p>
                    <p className="text-lg font-data font-bold text-violet-400">{fmtGdp(cats.gdp_per_capita?.latest_value, "currency")}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div>
            <h2 className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-3 font-medium">GDP Components</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="gdp-components-grid">
              {componentKeys.map(key => cats[key] && <ComponentCard key={key} category={key} data={cats[key]} />)}
            </div>
          </div>
        </div>
      )}

      {!loading && !error && view === "components" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="gdp-all-components">
          {allAvailable.map(([key, data]) => <HeroCard key={key} category={key} data={data} />)}
        </div>
      )}

      {report && (
        <div className="flex items-center justify-between pt-2 border-t border-zinc-800/50" data-testid="gdp-footer">
          <p className="text-[10px] text-zinc-600">Source: {report.source}</p>
          <div className="flex items-center gap-1 text-[10px] text-zinc-600">
            <Clock size={10} />
            <span>Updated: {new Date(report.report_date).toLocaleString()}</span>
          </div>
        </div>
      )}
    </div>
  );
}
